import React, { useState, useEffect, useRef } from 'react';
import {
  getCustomBlocks,
  saveCustomBlock,
  deleteCustomBlock,
  getCustomTemplates,
} from '../../services/quoteProposalService';
import type { CustomBlock, CustomTemplate } from '../../services/quoteProposalService';

interface BlockConfig {
  id: number;
  companyId?: number;
  templateId: number;
  blockType: string;
  title: string;
  htmlContent: string | null;
  fileUrl: string | null;
  fileName: string | null;
  placement: string;
  sortOrder: number;
  isActive: boolean;
}

const PLACEMENTS = [
  {
    key: 'AFTER_COVER',
    label: 'After Cover Page',
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18"/></svg>
    ),
    description: 'Content appears between the cover page and company overview',
  },
  {
    key: 'AFTER_COMPANY',
    label: 'After Company Page',
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
    ),
    description: 'Content appears between company overview and quote breakdown',
  },
  {
    key: 'AFTER_CONTACT',
    label: 'After Contact Page',
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
    ),
    description: 'Content appears after the contact information section',
  },
];

const TEMPLATE_FLOW_STEPS = [
  { label: 'Cover', slot: null },
  { label: null, slot: 'AFTER_COVER' },
  { label: 'Company', slot: null },
  { label: null, slot: 'AFTER_COMPANY' },
  { label: 'Breakdown', slot: null },
  { label: 'Comparison', slot: null },
  { label: 'Video', slot: null },
  { label: 'Acceptance', slot: null },
  { label: 'Contact', slot: null },
  { label: null, slot: 'AFTER_CONTACT' },
  { label: 'Extra Docs', slot: null },
];

interface Props {
  templateId: number;
  companyLogo?: string;
  customTemplateId?: number;
  onTemplateChange?: (templateId: number, customTemplateId?: number) => void;
  showToast: (type: 'success' | 'error' | 'warning', msg: string) => void;
}

const ProposalConfigurationSection: React.FC<Props> = ({
  templateId,
  companyLogo,
  customTemplateId,
  onTemplateChange,
  showToast,
}) => {
  const [savedBlocks, setSavedBlocks] = useState<BlockConfig[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(templateId);
  const [customTemplates, setCustomTemplates] = useState<CustomTemplate[]>([]);
  const [selectedCustomId, setSelectedCustomId] = useState<number | null>(customTemplateId || null);

  const [addingForPlacement, setAddingForPlacement] = useState<string | null>(null);
  const [formBlockType, setFormBlockType] = useState('FILE');
  const [formTitle, setFormTitle] = useState('');
  const [formContent, setFormContent] = useState('');
  const [formFile, setFormFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [savingId, setSavingId] = useState<string | null>(null);

  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewTitle, setPreviewTitle] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<BlockConfig | null>(null);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => { setSelectedTemplate(templateId); }, [templateId]);
  useEffect(() => { setSelectedCustomId(customTemplateId || null); }, [customTemplateId]);
  useEffect(() => { fetchBlocks(); }, [selectedTemplate]);
  useEffect(() => { fetchCustomTemplates(); }, []);

  const fetchBlocks = async () => {
    setLoading(true);
    const blocks = await getCustomBlocks(String(selectedTemplate));
    setSavedBlocks((blocks as unknown as BlockConfig[]).filter((b) => b.isActive));
    setLoading(false);
  };

  const fetchCustomTemplates = async () => {
    const templates = await getCustomTemplates();
    setCustomTemplates(templates);
  };

  const getBlocksForPlacement = (placement: string) =>
    savedBlocks.filter((b) => b.placement === placement);

  const getBlockCount = (slot: string) =>
    savedBlocks.filter((b) => b.placement === slot).length;

  const resetForm = () => {
    setAddingForPlacement(null);
    setFormBlockType('FILE');
    setFormTitle('');
    setFormContent('');
    setFormFile(null);
  };

  const handleSaveBlock = async () => {
    if (!addingForPlacement) return;
    if (!formTitle.trim()) { showToast('warning', 'Please enter a title'); return; }
    if (formBlockType === 'FILE' && !formFile) { showToast('warning', 'Please select a file'); return; }
    if (formBlockType !== 'FILE' && !formContent.trim()) { showToast('warning', 'Please enter content'); return; }

    setSavingId(addingForPlacement);
    const formData = new FormData();
    formData.append('templateId', String(selectedTemplate));
    formData.append('blockType', formBlockType);
    formData.append('placement', addingForPlacement);
    formData.append('title', formTitle.trim());
    formData.append('sortOrder', String(getBlocksForPlacement(addingForPlacement).length + 1));
    if (formBlockType === 'FILE' && formFile) {
      formData.append('file', formFile);
    } else {
      formData.append('htmlContent', formContent);
    }

    const ok = await saveCustomBlock(formData);
    setSavingId(null);
    if (ok) {
      showToast('success', 'Block saved');
      resetForm();
      await fetchBlocks();
    } else {
      showToast('error', 'Failed to save block');
    }
  };

  const handleDeleteBlock = async () => {
    if (!deleteConfirm) return;
    setSavingId(`delete-${deleteConfirm.id}`);
    const ok = await deleteCustomBlock(deleteConfirm.id);
    setSavingId(null);
    if (ok) {
      showToast('success', 'Block removed');
      setDeleteConfirm(null);
      await fetchBlocks();
    } else {
      showToast('error', 'Failed to delete block');
    }
  };

  const handleFileSelect = (file: File | null) => {
    if (!file) return;
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (!ext || !['pdf', 'jpg', 'jpeg', 'png'].includes(ext)) {
      showToast('warning', 'Only PDF, JPG or PNG files are allowed');
      return;
    }
    if (file.size > 10 * 1024 * 1024) { showToast('warning', 'Max file size is 10MB'); return; }
    setFormFile(file);
    if (!formTitle.trim()) setFormTitle(file.name.replace(/\.[^/.]+$/, ''));
  };

  const defaultTemplates = [
    { id: 1, title: 'Modern Grid' },
    { id: 2, title: 'Classic Compare' },
    { id: 3, title: 'Modern Template' },
  ];

  const handleTemplateSelect = (id: number, customId?: number) => {
    setSelectedTemplate(id);
    setSelectedCustomId(customId || null);
    onTemplateChange?.(id, customId);
  };

  return (
    <div className="pcb-container">
      <div className="qp-settings__section-header" style={{ marginBottom: 20 }}>
        <h3 className="qp-settings__section-title">Custom Content Blocks</h3>
        <p className="qp-settings__section-desc">
          Add custom files or text to specific sections of your proposal template.
        </p>
      </div>

      {/* Template selector chips */}
      <div className="pcb-template-selector">
        <span className="pcb-selector-label">Template</span>
        <div className="pcb-chips">
          {defaultTemplates.map((t) => (
            <button
              key={t.id}
              className={`pcb-chip${selectedTemplate === t.id && !selectedCustomId ? ' pcb-chip--active' : ''}`}
              onClick={() => handleTemplateSelect(t.id)}
            >
              {t.title}
            </button>
          ))}
          {customTemplates.map((t) => (
            <button
              key={`c-${t.id}`}
              className={`pcb-chip pcb-chip--custom${selectedCustomId === t.id ? ' pcb-chip--active' : ''}`}
              onClick={() => handleTemplateSelect(t.parentTemplateId || 1, t.id)}
            >
              {t.name}
              <span className="pcb-chip-tag">custom</span>
            </button>
          ))}
        </div>
        <p className="pcb-selector-hint">Select a template to configure its custom blocks.</p>
      </div>

      {/* Template flow visualizer */}
      <div className="pcb-flow">
        {TEMPLATE_FLOW_STEPS.map((step, i) => {
          if (step.slot) {
            const count = getBlockCount(step.slot);
            return (
              <React.Fragment key={`slot-${step.slot}`}>
                <div className="pcb-flow-arrow">›</div>
                <button
                  className={`pcb-flow-slot${count > 0 ? ' pcb-flow-slot--filled' : ''}`}
                  title={PLACEMENTS.find((p) => p.key === step.slot)?.label}
                  onClick={() => {
                    document.getElementById(`pcb-section-${step.slot}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                >
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                  {count > 0 && <span className="pcb-flow-count">{count}</span>}
                </button>
                <div className="pcb-flow-arrow">›</div>
              </React.Fragment>
            );
          }
          return (
            <React.Fragment key={`step-${i}`}>
              {i > 0 && !TEMPLATE_FLOW_STEPS[i - 1].slot && i !== 1 && <div className="pcb-flow-arrow">›</div>}
              <div className="pcb-flow-step">{step.label}</div>
            </React.Fragment>
          );
        })}
      </div>

      {loading ? (
        <div className="qp-settings__loader">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: 'spin 1s linear infinite' }}><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>
        </div>
      ) : (
        <>
          {PLACEMENTS.map((placement) => {
            const blocks = getBlocksForPlacement(placement.key);
            const isAdding = addingForPlacement === placement.key;
            return (
              <div key={placement.key} id={`pcb-section-${placement.key}`} className="pcb-section">
                <div className="pcb-section__header">
                  <div className="pcb-section__icon">{placement.icon}</div>
                  <div>
                    <div className="pcb-section__title">{placement.label}</div>
                    <div className="pcb-section__desc">{placement.description}</div>
                  </div>
                  {blocks.length > 0 && (
                    <span className="pcb-section__count">{blocks.length} block{blocks.length !== 1 ? 's' : ''}</span>
                  )}
                </div>

                {/* Existing blocks */}
                {blocks.length > 0 && (
                  <div className="pcb-blocks-list">
                    {blocks.map((block) => (
                      <div key={block.id} className="pcb-block">
                        <div className="pcb-block__left">
                          <span className={`pcb-block__type-badge pcb-block__type-badge--${block.blockType.toLowerCase()}`}>
                            {block.blockType}
                          </span>
                          <div>
                            <div className="pcb-block__title">{block.title}</div>
                            {block.blockType === 'FILE' && block.fileName && (
                              <div className="pcb-block__meta">
                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>
                                {block.fileName}
                              </div>
                            )}
                            {block.blockType !== 'FILE' && block.htmlContent && (
                              <div className="pcb-block__meta">
                                {block.htmlContent.replace(/<[^>]*>/g, '').substring(0, 80)}
                                {block.htmlContent.length > 80 ? '…' : ''}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="pcb-block__actions">
                          {block.blockType === 'FILE' && block.fileUrl && (
                            <button
                              className="pcb-block__action-btn"
                              onClick={() => { setPreviewUrl(block.fileUrl!); setPreviewTitle(block.title); }}
                              title="Preview"
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                            </button>
                          )}
                          <button
                            className="pcb-block__action-btn pcb-block__action-btn--delete"
                            onClick={() => setDeleteConfirm(block)}
                            disabled={savingId === `delete-${block.id}`}
                            title="Delete"
                          >
                            {savingId === `delete-${block.id}` ? (
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: 'spin 1s linear infinite' }}><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>
                            ) : (
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/></svg>
                            )}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add block form / button */}
                {isAdding ? (
                  <div className="pcb-form">
                    <div className="pcb-form__header">
                      <span className="pcb-form__title">Add Custom Block</span>
                      <button className="qp-modal__close" onClick={resetForm}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                      </button>
                    </div>

                    {/* Block type */}
                    <div className="pcb-form__field">
                      <label className="qp-settings__label">Block Type</label>
                      <div className="pcb-type-btns">
                        {[{ key: 'FILE', label: 'File Upload' }, { key: 'TEXT', label: 'Text' }].map((bt) => (
                          <button
                            key={bt.key}
                            className={`pcb-type-btn${formBlockType === bt.key ? ' pcb-type-btn--active' : ''}`}
                            onClick={() => { setFormBlockType(bt.key); setFormFile(null); setFormContent(''); }}
                          >
                            {bt.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Title */}
                    <div className="pcb-form__field">
                      <label className="qp-settings__label">Title</label>
                      <input
                        className="qp-settings__input"
                        type="text"
                        placeholder="Block title"
                        value={formTitle}
                        onChange={(e) => setFormTitle(e.target.value)}
                        maxLength={100}
                      />
                    </div>

                    {/* Content */}
                    <div className="pcb-form__field">
                      <label className="qp-settings__label">{formBlockType === 'FILE' ? 'Upload File' : 'Content'}</label>
                      {formBlockType === 'FILE' ? (
                        formFile ? (
                          <div className="pcb-selected-file">
                            <div className="pcb-selected-file__info">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                              <div>
                                <div className="pcb-selected-file__name">{formFile.name}</div>
                                <div className="pcb-selected-file__size">{(formFile.size / 1024 / 1024).toFixed(2)} MB</div>
                              </div>
                            </div>
                            <button className="pcb-block__action-btn pcb-block__action-btn--delete" onClick={() => setFormFile(null)}>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                            </button>
                          </div>
                        ) : (
                          <div
                            className={`pcb-upload-area${dragOver ? ' pcb-upload-area--drag' : ''}`}
                            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                            onDragLeave={() => setDragOver(false)}
                            onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFileSelect(Array.from(e.dataTransfer.files)[0] || null); }}
                            onClick={() => fileInputRef.current?.click()}
                          >
                            <input ref={fileInputRef} type="file" style={{ display: 'none' }} accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => handleFileSelect(e.target.files?.[0] || null)} />
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ marginBottom: 8 }}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                            <div className="pcb-upload-area__text">Drop file here or click to browse</div>
                            <div className="pcb-upload-area__hint">PDF, JPG or PNG — max 10MB</div>
                          </div>
                        )
                      ) : (
                        <textarea
                          className="qp-settings__textarea"
                          rows={5}
                          placeholder="Enter your text content here…"
                          value={formContent}
                          onChange={(e) => setFormContent(e.target.value)}
                        />
                      )}
                    </div>

                    <div className="pcb-form__actions">
                      <button className="qp-settings__save-btn" onClick={handleSaveBlock} disabled={!!savingId}>
                        {savingId === addingForPlacement ? (
                          <><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: 'spin 1s linear infinite' }}><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg> Saving…</>
                        ) : 'Save Block'}
                      </button>
                      <button className="qp-tpl-cancel-btn" onClick={resetForm}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <button
                    className="pcb-add-btn"
                    onClick={() => { resetForm(); setAddingForPlacement(placement.key); }}
                    disabled={!!savingId}
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                    Add Block
                  </button>
                )}
              </div>
            );
          })}
        </>
      )}

      {/* File Preview Modal */}
      {previewUrl && (
        <div className="qp-modal-overlay" onClick={() => setPreviewUrl(null)}>
          <div className="qp-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 800 }}>
            <div className="qp-modal__header">
              <span>{previewTitle}</span>
              <button className="qp-modal__close" onClick={() => setPreviewUrl(null)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div className="qp-modal__body">
              <iframe src={previewUrl} style={{ width: '100%', height: '70vh', border: 'none', borderRadius: 6 }} title="File Preview" />
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm modal */}
      {deleteConfirm && (
        <div className="qp-modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="qp-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 360 }}>
            <div className="qp-modal__header">
              <span>Remove Block</span>
              <button className="qp-modal__close" onClick={() => setDeleteConfirm(null)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div className="qp-modal__body">
              <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: 0 }}>
                Remove <strong style={{ color: 'var(--text-primary)' }}>"{deleteConfirm.title}"</strong>?
              </p>
            </div>
            <div className="qp-modal__footer">
              <button className="qp-tpl-delete-confirm-btn" onClick={handleDeleteBlock} disabled={!!savingId}>
                {savingId ? 'Removing…' : 'Remove'}
              </button>
              <button className="qp-tpl-cancel-btn" onClick={() => setDeleteConfirm(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProposalConfigurationSection;
